import { css, html, LitElement } from "lit";

import L from "leaflet";
import "../common/rotate.js";

import { fetchPlanes } from "../common/api.js";

const airplaneIcon = L.icon({
  iconUrl: "./images/plane.png",
  iconSize: [18, 18],
  iconAnchor: [9, 9],
  popupAnchor: [0, -16],
});

const airplaneIcon2 = L.icon({
  iconUrl: "./images/plane2.png",
  iconSize: [18, 18],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16],
});

class MapComponent extends LitElement {
  static get styles() {
    return [
      css`
        :host {
          margin-top: 5px;
          display: block;
          height: calc(100vh - 75px);
          background-color: #666;
        }
        button {
          position: absolute;
          right: 10px;
          top: 100px;
          z-index: 999;
          cursor: pointer;
        }
        @media (min-width: 768px) {
            :host {
                margin-top: 25px;
                height: calc(100vh - 95px);
            }
        }
      `,
    ];
  }

  constructor() {
    super();
    /** @type {Map<string, any>} */
    this.markers = new Map();
    this.map = null;
    this.hideBack = false;
    this.currPolyLine = null;
    this.currWaypoints = null;
    this.currMarker = null;
    this.timer = null;
  }

  firstUpdated() {
    /** @type {HTMLElement} */
    const mapEl = this.shadowRoot.querySelector("#mapid");
    this.map = L.map(mapEl).setView([51.505, -0.09], 3);

    const urlTemplate =
      "http://services.arcgisonline.com/arcgis/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}";
    this.map.addLayer(L.tileLayer(urlTemplate));
    this.loadData();
    this.timer = setInterval(() => this.loadData(), 30000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  /**
   * Filter the map markers.
   */
  filterMarkers() {
    this.markers.forEach((marker) => {
      if (this.hideBack && marker.network !== "PlanePals") {
        marker.setLatLng([0, 0]);
      } else {
        marker.setLatLng(marker.currpos);
      }
    });
  }

  /**
   * Draw a polyline for the given position array.
   * @param {Array<[number, number]>} posArray - An array of positions to draw the polyline.
   */
  drawPolyline(posArray) {
    if (this.currPolyLine) {
      this.map.removeLayer(this.currPolyLine);
    }
    this.currPolyLine = null;
    if (!this.currMarker || !posArray) return;
    this.currPolyLine = L.polyline(posArray, {
      color: "#3a65e8",
      weight: 6,
      opacity: 0.7,
      smoothFactor: 0,
    }).addTo(this.map);
  }

  /**
   * Draw waypoints on the map.
   * @param {Array<[number, number]>} waypoints - An array of waypoint positions to draw.
   */
  drawWaypoints(waypoints) {
    if (this.currWaypoints) {
      this.map.removeLayer(this.currWaypoints);
    }
    this.currWaypoints = null;
    if (!waypoints) return;
    this.currWaypoints = L.polyline(waypoints, {
      color: "#FFF",
      weight: 5,
      opacity: 0.7,
      smoothFactor: 0,
    }).addTo(this.map);
  }

  /**
   * Load the plane data from the server and update the map markers accordingly.
   */
  async loadData() {
    const clients = await fetchPlanes();
    /** @type {Array<string>} */
    const active = [];

    clients?.forEach((client) => {
      active.push(client.user);
      let airplaneMarker = this.markers.get(client.user);
      if (!airplaneMarker) {
        airplaneMarker = L.marker([client.lat, client.lon], {
          icon: client.network === "PlanePals" ? airplaneIcon2 : airplaneIcon,
          // @ts-expect-error
          rotationAngle: client.heading,
        }).addTo(this.map);
        airplaneMarker.network = client.network;
        airplaneMarker.prevPositions = client.prev || [];
        airplaneMarker.on("click", () => {
          this.currMarker = airplaneMarker;
          this.drawWaypoints(client.waypoints);
          this.drawPolyline(airplaneMarker.prevPositions);
        });
        this.markers.set(client.user, airplaneMarker);
      } else {
        airplaneMarker.setLatLng([client.lat, client.lon]);
        airplaneMarker.setRotationAngle(client.heading);
      }
      airplaneMarker.prevPositions.unshift([client.lat, client.lon]);
      airplaneMarker.currpos = [client.lat, client.lon];
      airplaneMarker.planetooltip = `
      <b>${client.user}</b><br>
      ${client.callsign ? `<b>${client.callsign}</b><br>` : ""}
      ${client.waypointNames?.length >= 2 ? `<b>Route:</b> ${client.waypointNames[0]} - ${client.waypointNames[client.waypointNames.length - 1]}<br>` : ""}
      <b>Aircraft:</b> ${client.icao}<br>
      <b>Speed:</b> ${Math.floor(client.speed)} kt<br>
      <b>Alt:</b> ${
        client.alt > 10000
          ? `FL${Math.floor(client.alt / 100)}`
          : `${Math.floor(client.alt)}ft`
      }<br>
      <small>${client.network} / ${client.sim}</small>`;
      airplaneMarker.bindPopup(airplaneMarker.planetooltip);
    });

    Array.from(this.markers.keys()).forEach((user) => {
      if (active.indexOf(user) === -1) {
        const marker = this.markers.get(user);
        marker.remove();
        this.markers.delete(user);
      }
    });

    this.filterMarkers();
    this.drawPolyline(this.currMarker?.prevPositions);
  }

  render() {
    return html`
      <link
        rel="stylesheet"
        href="https://cdn.skypack.dev/leaflet/dist/leaflet.css"
      />
      <button
        @click=${() => {
          this.hideBack = !this.hideBack;
          this.filterMarkers();
          this.requestUpdate();
        }}
      >
        ${this.hideBack ? "Show" : "Hide"} Background Traffic
      </button>
      <div id="mapid" style="height: 100%"></div>
    `;
  }
}

customElements.define("map-component", MapComponent);
